import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefSelect } from "@/components/data/FieldInput";
import { SimpleTable } from "@/components/data/SimpleTable";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtMoney, monthStart, today } from "@/lib/api";

const LEDGER = [
  { k: "libelle", label: "Tiers" }, { k: "initial", label: "Solde initial", type: "money" }, { k: "mouvements", label: "Mouvements", type: "money" },
  { k: "reglements", label: "Règlements", type: "money" }, { k: "final", label: "Solde final", type: "money" },
  { k: "echeance", label: "Échéance", type: "date" }, { k: "retard", label: "Retard (j)" },
];
const COMPTE = { name: "c", type: "ref", ref: "comptes" };

function Periode({ p, setP }) {
  return (
    <div className="flex gap-2 items-center">
      <Input type="date" className="h-9 w-[150px]" value={p.date_from} onChange={(e) => setP({ ...p, date_from: e.target.value })} data-testid="situation-date-from" />
      <span className="text-xs text-muted-foreground">au</span>
      <Input type="date" className="h-9 w-[150px]" value={p.date_to} onChange={(e) => setP({ ...p, date_to: e.target.value })} data-testid="situation-date-to" />
    </div>
  );
}

export function Situation() {
  const { can } = useAuth();
  const [p, setP] = useState({ date_from: monthStart(), date_to: today() });
  const [d, setD] = useState(null);
  const [f, setF] = useState({ montant: "", compte_id: null, reference: "" });
  const [k, setK] = useState(0);
  useEffect(() => {
    api.get("/dettes/situation", { params: p }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p, k]);
  const payer = async () => {
    try {
      await api.post("/partenaire/paiements", f);
      toast.success("Règlement partenaire enregistré");
      setF({ montant: "", compte_id: null, reference: "" });
      setK((n) => n + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!d) return <div className="text-sm text-muted-foreground">Chargement…</div>;
  const pa = d.partenaire;
  return (
    <div className="space-y-5">
      <Periode p={p} setP={setP} />
      <SimpleTable title="Dettes envers les superviseurs (commissions dues)" module="dettes" testId="situation-dettes-superviseurs" rows={d.dettes_superviseurs} cols={LEDGER} />
      <SimpleTable title="Dettes envers les revendeurs (commissions nettes dues et excédents)" module="dettes" testId="situation-dettes-revendeurs" rows={d.dettes_revendeurs} cols={LEDGER} />
      <SimpleTable title="Créances sur les superviseurs (remontées attendues non encaissées)" module="dettes" testId="situation-creances-superviseurs" rows={d.creances_superviseurs} cols={LEDGER} />
      <div className="bg-card border rounded-lg p-4 space-y-3" data-testid="situation-partenaire">
        <h3 className="text-xs font-bold uppercase tracking-wider">Dette partenaire — {pa.nom}</h3>
        <p className="text-[11px] text-muted-foreground">Règle : {pa.regles}. Montants calculés, distincts des règlements effectivement payés.</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
          {[["CA période", pa.ca], ["Gains", pa.gains], ["Com. revendeurs", pa.com_revendeurs], ["Com. superviseurs", pa.com_superviseurs], ["Com. KGD", pa.com_kgd],
            ["Solde initial", pa.initial], ["Dû sur la période", pa.mouvements], ["Réglé sur la période", pa.reglements]].map(([l, v]) => (
            <div key={l} className="p-2 rounded border"><div className="text-muted-foreground">{l}</div><div className="font-mono font-semibold">{fmtMoney(v)}</div></div>
          ))}
        </div>
        <div className="font-mono text-lg font-bold" data-testid="partenaire-solde-final">Solde final dû : {fmtMoney(pa.final)}</div>
        {can("dettes", "valider") && (
          <div className="flex flex-wrap gap-2 items-end border-t pt-3">
            <div><Label>Montant réglé</Label><Input type="number" className="h-9 w-40 font-mono" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} data-testid="partenaire-montant" /></div>
            <div className="w-56"><Label>Compte</Label><RefSelect field={COMPTE} value={f.compte_id} onChange={(v) => setF({ ...f, compte_id: v })} testId="partenaire-compte" /></div>
            <div><Label>Référence</Label><Input className="h-9" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></div>
            <Button size="sm" onClick={payer} disabled={!f.montant || !f.compte_id} data-testid="partenaire-payer">Enregistrer le règlement</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Rapprochement() {
  const [p, setP] = useState({ date_from: monthStart(), date_to: today() });
  const [d, setD] = useState({ ecarts: [] });
  useEffect(() => {
    api.get("/rapprochement", { params: p }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p]);
  return (
    <div className="space-y-3">
      <Periode p={p} setP={setP} />
      <p className="text-xs text-muted-foreground">Contrôle CA, gains, commissions, encaissements et sommes dues. Les écarts sont signalés, jamais corrigés automatiquement. Rouge = incohérence, blanc = information.</p>
      <SimpleTable title={`Rapprochement — ${d.ecarts.length} point(s) signalé(s)`} module="dettes" testId="rapprochement-table" totals={false}
        rows={d.ecarts} cols={[{ k: "date", label: "Date", type: "date" }, { k: "controle", label: "Contrôle" }, { k: "attendu", label: "Attendu", type: "money" },
          { k: "constate", label: "Constaté", type: "money" }, { k: "ecart", label: "Écart", type: "money" }]} />
    </div>
  );
}
